import React, { useEffect } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { MediaCell, cellImage } from "../ProspectTable";

const PEEK_ROWS = 5;
const PEEK_COLS = 3;

const cellText = (row, col) => {
  const v = row && row[col.key];
  if (v == null || v === "") return "";
  return typeof v === "object" ? "" : String(v);
};

/**
 * Result-table artifact card. Shows the first rows inline (a "peek") so the
 * user sees what came back without opening anything, fading out under a
 * floating CTA that opens the docked panel. Rows come from the store's table
 * entry; the first page is fetched on mount when it is not loaded yet.
 */
const TableRefBlock = observer(({ block }) => {
  // The store's table entry is the live count (kept in sync by corrections
  // and row removals); the block payload is the fallback before it exists.
  const table = copilotStore.tables.get(block.tableId);
  // A store count of 0 while the search reported matches means the page has
  // not been counted yet, not an empty result; keep the block's figure.
  const totalCount = (table && table.totalCount) || block.totalCount || 0;
  const rows = (table && table.rows) || [];
  // Onboarding loads only the top matches of a much larger search; say so
  // instead of calling the whole search "10 records".
  const matchTotal = Number(block.matchTotal) || 0;
  const countLabel =
    matchTotal > totalCount
      ? `Top ${totalCount.toLocaleString()} of ${matchTotal.toLocaleString()} matches`
      : `${totalCount.toLocaleString()} ${totalCount === 1 ? "record" : "records"} · live table`;
  const cols = (block.columns || []).slice(0, PEEK_COLS);
  const open = () => copilotStore.openTablePanel(block.tableId);

  useEffect(() => {
    if (table && !rows.length && !table.loading && !table.stale && totalCount > 0) {
      copilotStore.fetchTablePage(block.tableId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.tableId, Boolean(table)]);

  const peek = rows.slice(0, PEEK_ROWS);
  // A few faces (or logos) from the result, stacked on the CTA: the button
  // shows who is behind the number.
  const faceCol = cols.find(c => c.type === "avatar") || cols.find(c => c.type === "logo");
  const faces = faceCol ? rows.map(r => cellImage(r, faceCol)).filter(Boolean).slice(0, 3) : [];
  const showPeek = cols.length > 0 && (peek.length > 0 || (table && table.loading));

  // A background lookup: the table is registered (so a name link can open it) but not drawn.
  if (block.hidden) return null;
  return (
    <div className="copilotTableCard" onClick={open}>
      <div className="copilotTableCard__head">
        <div className="copilotTableCard__icon">
          <i className="material-icons-outlined">grid_on</i>
        </div>
        <div className="copilotTableCard__meta">
          <div className="copilotTableCard__title">{block.title}</div>
          <div className="copilotTableCard__count">
            {countLabel}
          </div>
        </div>
        {!showPeek && (
          <div className="copilotTableCard__open">
            <span>View</span>
            <i className="material-icons-outlined">chevron_right</i>
          </div>
        )}
      </div>

      {showPeek && (
        <div className="copilotTableCard__peek">
          <div className="copilotTableCard__peekRows">
            <div className="copilotTableCard__peekRow is-head">
              {cols.map(c => (
                <span className="copilotTableCard__peekCell" key={c.key}>
                  {c.label || c.key}
                </span>
              ))}
            </div>
            {peek.length === 0 &&
              Array.from({ length: 3 }).map((_, i) => (
                <div className="copilotTableCard__peekRow is-skeleton" key={`s${i}`}>
                  {cols.map(c => (
                    <span className="copilotTableCard__peekCell" key={c.key} />
                  ))}
                </div>
              ))}
            {peek.map((row, i) => (
              <div className="copilotTableCard__peekRow" key={row.id || i}>
                {cols.map((c, j) => (
                  <span
                    className={`copilotTableCard__peekCell ${j === 0 ? "is-primary" : ""}`}
                    key={c.key}
                    title={cellText(row, c)}
                  >
                    {/* Same avatar/logo cells as the full table, so the peek
                        looks like List Builder, not a text dump. */}
                    {c.type === "avatar" || c.type === "logo" ? (
                      <MediaCell
                        text={cellText(row, c)}
                        imageUrl={cellImage(row, c)}
                        round={c.type === "avatar"}
                      />
                    ) : (
                      cellText(row, c)
                    )}
                  </span>
                ))}
              </div>
            ))}
          </div>
          <div className="copilotTableCard__fade" />
          <button
            type="button"
            className="copilotTableCard__cta"
            onClick={e => {
              e.stopPropagation();
              open();
            }}
          >
            <i className="material-icons-outlined">grid_on</i>
            {matchTotal > totalCount ? `Review top ${totalCount}` : `View ${totalCount.toLocaleString()} ${totalCount === 1 ? "record" : "records"}`}
            {faces.length > 0 && (
              <span className="copilotTableCard__faces" aria-hidden="true">
                {faces.map((src, i) => (
                  <img key={i} src={src} alt="" loading="lazy" onError={e => { e.target.style.display = "none"; }} />
                ))}
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
});

export default TableRefBlock;
